import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';

interface Patient {
  id: string;
  fullName: string;
  phone: string | null;
  cpf: string | null;
}
interface Appointment {
  id: string;
  startsAt: string;
  patientName: string;
  patientId: string;
  status: string;
}
interface Invoice {
  id: string;
  code: string | null;
  patientName: string;
  status: string;
}
interface Service {
  id: string;
  code: string;
  name: string;
}
interface SearchResponse {
  patients: Patient[];
  appointments: Appointment[];
  invoices: Invoice[];
  services: Service[];
}

interface Item {
  key: string;
  groupLabel: string;
  title: string;
  subtitle?: string;
  onSelect: () => void;
}

function useDebounce<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Command Palette (Ctrl/Cmd+K). Aberto via hook global abaixo.
 * Mostra resultados de pacientes, agendamentos, faturas e serviços.
 *
 * Decisão: render condicional (só existe no DOM quando aberto). Isso
 * evita fetch residual ao carregar o shell e foca render no momento do
 * uso.
 */
export function CommandPalette({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();

  const debounced = useDebounce(query, 180);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      // timeout curto pra garantir foco após render.
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open]);

  useEffect(() => {
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && open) onClose();
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  const { data, isFetching } = useQuery<SearchResponse>({
    queryKey: ['search', debounced],
    queryFn: async () =>
      (await api.get('/search', { params: { q: debounced } })).data,
    enabled: open && debounced.length >= 2,
    staleTime: 30_000,
  });

  const items: Item[] = [];
  if (data) {
    data.patients.forEach((p) =>
      items.push({
        key: `patient-${p.id}`,
        groupLabel: 'Pacientes',
        title: p.fullName,
        subtitle: p.phone ?? p.cpf ?? undefined,
        onSelect: () => {
          navigate(`/pacientes/${p.id}`);
          onClose();
        },
      }),
    );
    data.appointments.forEach((a) => {
      const d = new Date(a.startsAt);
      items.push({
        key: `appt-${a.id}`,
        groupLabel: 'Agendamentos',
        title: `${a.patientName} · ${d.toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
        })}`,
        subtitle: `Status: ${a.status}`,
        onSelect: () => {
          navigate(`/pacientes/${a.patientId}?tab=appointments`);
          onClose();
        },
      });
    });
    data.invoices.forEach((inv) =>
      items.push({
        key: `invoice-${inv.id}`,
        groupLabel: 'Faturas',
        title: `${inv.patientName}${inv.code ? ` · ${inv.code}` : ''}`,
        subtitle: `Status: ${inv.status}`,
        onSelect: () => {
          navigate(`/faturas?open=${inv.id}`);
          onClose();
        },
      }),
    );
    data.services.forEach((s) =>
      items.push({
        key: `service-${s.id}`,
        groupLabel: 'Serviços',
        title: `${s.code} — ${s.name}`,
        onSelect: () => {
          navigate(`/servicos/${s.id}`);
          onClose();
        },
      }),
    );
  }

  // Reset active quando lista muda.
  useEffect(() => {
    setActiveIdx(0);
  }, [items.length]);

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, items.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const it = items[activeIdx];
      if (it) it.onSelect();
    }
  };

  if (!open) return null;

  // Agrupa resultados por grupo pra exibição.
  const grouped: Record<string, Item[]> = {};
  items.forEach((it) => {
    (grouped[it.groupLabel] ??= []).push(it);
  });

  let cursor = 0;

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-900/40 flex items-start justify-center pt-24 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Busca global"
      data-testid="command-palette"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="w-full max-w-xl rounded-xl bg-white shadow-xl border border-ink-200 overflow-hidden">
        <div className="px-3 py-2 border-b border-ink-200 flex items-center gap-2">
          <span className="text-ink-400 text-sm" aria-hidden>
            🔍
          </span>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKey}
            placeholder="Buscar pacientes, agendamentos, faturas, serviços…"
            className="flex-1 text-sm outline-none py-1.5"
            data-testid="command-palette-input"
            autoComplete="off"
          />
          <span className="text-[10px] text-ink-400 font-mono hidden sm:inline">
            Esc
          </span>
        </div>

        <div className="max-h-[50vh] overflow-y-auto">
          {debounced.length < 2 && (
            <p className="px-4 py-6 text-sm text-ink-400 text-center">
              Digite ao menos 2 caracteres.
            </p>
          )}
          {debounced.length >= 2 && isFetching && !data && (
            <p className="px-4 py-6 text-sm text-ink-400 text-center">
              Buscando…
            </p>
          )}
          {debounced.length >= 2 && data && items.length === 0 && (
            <p
              className="px-4 py-6 text-sm text-ink-400 text-center"
              data-testid="command-palette-empty"
            >
              Nenhum resultado para "{debounced}".
            </p>
          )}
          {Object.entries(grouped).map(([group, list]) => (
            <div key={group}>
              <div className="px-3 py-1 bg-ink-50 text-[11px] uppercase tracking-wide text-ink-500 border-t border-ink-100">
                {group}
              </div>
              {list.map((it) => {
                const idx = cursor++;
                const active = idx === activeIdx;
                return (
                  <button
                    key={it.key}
                    onClick={it.onSelect}
                    onMouseEnter={() => setActiveIdx(idx)}
                    data-testid={`command-palette-item-${it.key}`}
                    className={`block w-full text-left px-4 py-2 text-sm ${
                      active
                        ? 'bg-brand-50 text-brand-900'
                        : 'text-ink-700 hover:bg-ink-50'
                    }`}
                  >
                    <div className="font-medium truncate">{it.title}</div>
                    {it.subtitle && (
                      <div className="text-xs text-ink-500 truncate">
                        {it.subtitle}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        <div className="px-3 py-1.5 border-t border-ink-100 flex items-center justify-between text-[10px] text-ink-400">
          <span>↑↓ navegar · Enter selecionar · Esc fechar</span>
          <span>Ctrl/⌘+K abre</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook global: registra o atalho Ctrl/Cmd+K e retorna { open, setOpen }.
 * Usar em um componente alto (AppShell) pra manter o palette sempre
 * disponível.
 */
export function useCommandPaletteHotkey() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen };
}
